/**
 * Edupra brand assets — yantra-grid mark family.
 *
 * Logomark concept: a 4×4 lattice of small squares, with a single emerald
 * cell at row 3 col 2 (off-centre). The grid reads as:
 *   • a school register / ledger page (rows × columns of records)
 *   • a yantra (precise geometric Indian visual heritage)
 *   • a database table (one cell highlighted = the active entry)
 *   • a calendar / monthly grid (the current day)
 *
 * The mark extends naturally into the rest of the design system:
 *   - section dividers can be 1×N grid strips
 *   - empty states can be grids with one cell lit
 *   - tenant tiles can be 4×4 grids with progressively more cells lit
 *
 * Sizes:
 *   - 64 px (hero) – 4×4 grid, generous gutters
 *   - 32 px (app header) – 4×4 grid, tight gutters
 *   - 16 px (favicon) – collapses to 3×3 grid
 *
 * Wordmark: lowercase "edupra" in Geist Medium with -0.035em tracking.
 * Lowercase because schools are warm; period at end of taglines carries
 * the gravitas.
 */
import { Link } from 'react-router';
import { CSSProperties } from 'react';

/* ---------------------------------------------------------------------- */
/* Logomark variants                                                      */
/* ---------------------------------------------------------------------- */

interface MarkProps {
  size?: number;
  className?: string;
  /**
   * Override the accent cell position (row 0-3, col 0-3). Defaults to {row:2,col:1}
   * which is the off-centre "active row" that breaks rigidity.
   */
  accent?: { row: number; col: number };
}

/** 4×4 yantra-grid — the canonical mark. Use ≥ 24px. */
export function Mark4x4({ size = 32, className, accent = { row: 2, col: 1 } }: MarkProps) {
  // 4×4 grid in a 32-unit viewBox.
  // Each cell: 5×5 units. Gutter: 2 units. Padding: 2 units.
  // Layout (rows × cols): 2 + 5*4 + 2*3 + 2 = 32 ✓
  const cells: Array<[number, number]> = [];
  for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) cells.push([r, c]);
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="none"
      role="img"
      aria-label="Edupra"
      className={className}
    >
      {cells.map(([r, c]) => {
        const x = 2 + c * 7;
        const y = 2 + r * 7;
        const isAccent = r === accent.row && c === accent.col;
        return (
          <rect
            key={`${r}-${c}`}
            x={x}
            y={y}
            width={5}
            height={5}
            rx={0.75}
            style={{ fill: isAccent ? 'var(--color-accent-500)' : 'currentColor' }}
          />
        );
      })}
    </svg>
  );
}

/** 3×3 reduction for the favicon — same family, fewer cells. */
export function Mark3x3({ size = 16, className, accent = { row: 1, col: 1 } }: MarkProps) {
  // 3×3 grid in a 16-unit viewBox.
  // Each cell: 3.5×3.5 units. Gutter: 1.25 units. Padding: 1.25 units.
  const cells: Array<[number, number]> = [];
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) cells.push([r, c]);
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      role="img"
      aria-label="Edupra"
      className={className}
    >
      {cells.map(([r, c]) => {
        const x = 1.25 + c * 4.75;
        const y = 1.25 + r * 4.75;
        const isAccent = r === accent.row && c === accent.col;
        return (
          <rect
            key={`${r}-${c}`}
            x={x}
            y={y}
            width={3.5}
            height={3.5}
            rx={0.5}
            style={{ fill: isAccent ? 'var(--color-accent-500)' : 'currentColor' }}
          />
        );
      })}
    </svg>
  );
}

/** Auto-size-based picker — uses 3×3 for ≤ 18px, 4×4 above. */
export function Logomark({ size = 28, className, accent }: MarkProps) {
  if (size <= 18) return <Mark3x3 size={size} className={className} accent={accent} />;
  return <Mark4x4 size={size} className={className} accent={accent} />;
}

/* ---------------------------------------------------------------------- */
/* Wordmark variants                                                      */
/* ---------------------------------------------------------------------- */

type WordmarkWeight = 'regular' | 'medium' | 'semibold' | 'bold';
type WordmarkCase = 'lower' | 'sentence' | 'upper';

interface WordmarkProps {
  size?: number;
  weight?: WordmarkWeight;
  case?: WordmarkCase;
  tracking?: number;
  className?: string;
  /** Underline the `up` ligature with a thin emerald stroke (custom accent). */
  ligature?: boolean;
}

const FONT_WEIGHTS: Record<WordmarkWeight, number> = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export function Wordmark({
  size = 18,
  weight = 'medium',
  case: textCase = 'lower',
  tracking = -0.035,
  className,
  ligature = false,
}: WordmarkProps) {
  const text = textCase === 'upper' ? 'EDUPRA' : textCase === 'sentence' ? 'Edupra' : 'edupra';

  const style: CSSProperties = {
    fontFamily: 'Geist, system-ui, sans-serif',
    fontWeight: FONT_WEIGHTS[weight],
    letterSpacing: `${tracking}em`,
    fontSize: `${size}px`,
    color: 'var(--fg-high)',
    lineHeight: 1,
    fontFeatureSettings: '"ss01"',
    display: 'inline-block',
  };

  if (!ligature) return <span className={className} style={style}>{text}</span>;

  // For the ligature variant, underline `up` with a thin emerald rule.
  // Works only on lowercase / sentence-case (where `up` is present).
  const idx = text.toLowerCase().indexOf('up');
  if (idx === -1) return <span className={className} style={style}>{text}</span>;
  const before = text.slice(0, idx);
  const mid = text.slice(idx, idx + 2);
  const after = text.slice(idx + 2);
  return (
    <span className={className} style={style}>
      {before}
      <span
        style={{
          borderBottom: `2px solid var(--color-accent-500)`,
          paddingBottom: `${Math.round(size * 0.06)}px`,
        }}
      >
        {mid}
      </span>
      {after}
    </span>
  );
}

/* ---------------------------------------------------------------------- */
/* Lockup                                                                  */
/* ---------------------------------------------------------------------- */

interface BrandProps {
  variant?: 'full' | 'mark';
  size?: number;
  href?: string | null;
  className?: string;
  weight?: WordmarkWeight;
}

export function Brand({
  variant = 'full',
  size = 28,
  href = '/',
  className,
  weight = 'medium',
}: BrandProps) {
  const inner = (
    <span className="inline-flex items-center gap-2 select-none">
      <Logomark size={size} />
      {variant === 'full' && <Wordmark size={Math.round(size * 0.7)} weight={weight} />}
    </span>
  );
  if (!href) return <span className={className}>{inner}</span>;
  return (
    <Link to={href} className={className} aria-label="Edupra home">
      {inner}
    </Link>
  );
}
